import { addDays, lastCompleteDay } from '../ProductPerformance/model';

import { type ReportState, defaultSendFrom } from './presentationModel';

/** Report tab keys, as used in the Lens request's worksheet_view. */
export type ViewKey = 'tables' | 'comms' | 'financial' | 'health' | 'clicks' | 'regions' | 'offers' | 'vendors';
export type ViewFilter = { key: string; label: string };

/** URL value for "All" on a filter whose Tableau default is a selection, so All survives a reload. */
export const ALL_SELECTED = '[]';

/**
 * First send date in analytics.comms_reporting_click_level (checked 21 Oct 2026). Tableau's click tabs open
 * on every send date, and its range filter shows 17/10/2023 as the lower bound, so this is the same window.
 */
export const CLICK_SOURCE_START = '2023-10-17';

const COUNTRY: ViewFilter = { key: 'subscriber_country', label: 'Country' };
const REGION: ViewFilter = { key: 'subscriber_region', label: 'Region' };
const UTM_SOURCE: ViewFilter = { key: 'utm_source', label: 'UTM Source' };
const UTM_POSITION: ViewFilter = { key: 'utm_position', label: 'UTM Position' };
const UTM_CONTENT: ViewFilter = { key: 'utm_content', label: 'UTM Content' };
const TE_CADENCE: ViewFilter = { key: 'te_cadence', label: 'TE Cadence' };
const SEGMENT: ViewFilter = { key: 'segment_name', label: 'Segment Name' };
const MESSAGE: ViewFilter = { key: 'message_name', label: 'Message Name' };
const CAMPAIGN: ViewFilter = { key: 'campaign_name', label: 'Campaign Name' };
const JOURNEY: ViewFilter = { key: 'journey_name', label: 'Journey Name' };
// The click source has no brand_name; brand_bk holds the same brand values (luxuryescapes, scoopon, cudo...).
const CLICK_BRAND: ViewFilter = { key: 'brand_bk', label: 'Brand Name' };
const VENDOR: ViewFilter = { key: 'vendor_name', label: 'Vendor Name' };
const OFFER: ViewFilter = { key: 'offer_name', label: 'Offer Name' };
const CATEGORY: ViewFilter = { key: 'category_name', label: 'Category Name' };
const OFFER_COUNTRY: ViewFilter = { key: 'offer_country_name', label: 'Offer Country' };
const OFFER_REGION: ViewFilter = { key: 'offer_geo_area_level_1', label: 'Offer Region' };
const PRODUCT: ViewFilter = { key: 'product_name', label: 'Product Name' };
const DEAL_TIER: ViewFilter = { key: 'deal_tier', label: 'Deal Tier' };
const HERO_TAIL: ViewFilter = { key: 'hero_tail', label: 'Hero Tail' };
const EMAIL_PLACEMENT: ViewFilter = { key: 'email_placement', label: 'Email Placement' };

const SEND_FILTERS: Array<ViewFilter> = [
  COUNTRY,
  REGION,
  UTM_SOURCE,
  TE_CADENCE,
  { key: 'brand_name', label: 'Brand Name' },
  { key: 'campaign_journey', label: 'Campaign + Journey' },
  SEGMENT,
  MESSAGE,
  { key: 'platform', label: 'Platform' },
  { key: 'experiment_name', label: 'Experiment Name' },
];

/**
 * Quick filters on each Tableau dashboard, beyond the shared Country Group, UTM Medium and UTM Campaign controls.
 * Clicks Insights keeps its own single Product Name control beside the Clicks By Date chart.
 */
export const VIEW_FILTERS: Record<ViewKey, Array<ViewFilter>> = {
  tables: SEND_FILTERS,
  comms: SEND_FILTERS,
  financial: SEND_FILTERS,
  health: SEND_FILTERS,
  clicks: [COUNTRY, REGION, UTM_SOURCE, SEGMENT, MESSAGE, CLICK_BRAND, UTM_POSITION, UTM_CONTENT, VENDOR, OFFER],
  regions: [COUNTRY, REGION, UTM_SOURCE, MESSAGE, CLICK_BRAND, UTM_POSITION, UTM_CONTENT, VENDOR, OFFER],
  offers: [
    SEGMENT,
    CAMPAIGN,
    JOURNEY,
    TE_CADENCE,
    COUNTRY,
    REGION,
    CATEGORY,
    OFFER_COUNTRY,
    PRODUCT,
    OFFER_REGION,
    CLICK_BRAND,
    VENDOR,
    UTM_SOURCE,
    OFFER,
    DEAL_TIER,
    UTM_CONTENT,
    HERO_TAIL,
    EMAIL_PLACEMENT,
    MESSAGE,
  ],
  vendors: [
    CAMPAIGN,
    JOURNEY,
    TE_CADENCE,
    HERO_TAIL,
    EMAIL_PLACEMENT,
    SEGMENT,
    MESSAGE,
    CATEGORY,
    COUNTRY,
    PRODUCT,
    REGION,
    CLICK_BRAND,
    OFFER_COUNTRY,
    VENDOR,
    OFFER_REGION,
    OFFER,
    UTM_SOURCE,
    DEAL_TIER,
    UTM_CONTENT,
  ],
};

const SEND_DEFAULTS = { platform: ['braze'] };
/**
 * Tableau's saved selections. Lens reads a null Journey Name as Unknown, and filter values arrive trimmed,
 * so the saved Email Placement values ' 1', ' 2', ' 3' are sent as '1', '2', '3'.
 */
export const VIEW_FILTER_DEFAULTS: Record<ViewKey, Record<string, Array<string>>> = {
  tables: SEND_DEFAULTS,
  comms: SEND_DEFAULTS,
  financial: SEND_DEFAULTS,
  health: SEND_DEFAULTS,
  clicks: {},
  regions: {},
  offers: {},
  vendors: {
    journey_name: ['Unknown'],
    te_cadence: ['Daily'],
    hero_tail: ['Hero'],
    email_placement: ['1', '2', '3', 'Hero 1', 'Hero 2', 'Hero 3', 'Hero 4'],
  },
};

/** Every URL key a view filter can use, for Reset. */
export const ALL_VIEW_FILTER_KEYS = [
  ...new Set(Object.values(VIEW_FILTERS).flatMap((filters) => filters.map(({ key }) => key))),
];

/** The selection in force: the address when it names one, otherwise the tab's Tableau default. */
export function viewFilterValues(params: URLSearchParams, view: ViewKey, key: string): Array<string> {
  if (!params.has(key)) {
    return VIEW_FILTER_DEFAULTS[view][key] ?? [];
  }
  const values = params.getAll(key);
  return values.length === 1 && values[0] === ALL_SELECTED ? [] : values.filter((value) => value !== ALL_SELECTED);
}

const sameMembers = (a: Array<string>, b: Array<string>) =>
  a.length === b.length && [...a].sort().every((value, i) => value === [...b].sort()[i]);

/** Write a selection; the tab default stays out of the address, and All is written only when it differs. */
export function withViewFilter(
  params: URLSearchParams,
  view: ViewKey,
  key: string,
  values: Array<string>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete(key);
  const fallback = VIEW_FILTER_DEFAULTS[view][key] ?? [];
  if (sameMembers(values, fallback)) {
    return next;
  }
  if (!values.length) {
    next.append(key, ALL_SELECTED);
    return next;
  }
  values.forEach((value) => next.append(key, value));
  return next;
}

/** Non-empty selections for this tab's filters, ready for the Lens request. */
export function activeViewFilters(params: URLSearchParams, view: ViewKey): Record<string, Array<string>> {
  return Object.fromEntries(
    VIEW_FILTERS[view]
      .map(({ key }) => [key, viewFilterValues(params, view, key)] as const)
      .filter(([, values]) => values.length > 0),
  );
}

/**
 * Send dates for the Strategic Regions, Offer Clicks and Offer Vendor worksheets.
 * Tableau opens Strategic Regions and Offer Vendor on every send date. Offer Clicks also does, but its two
 * worksheets keep one row per send date and offer, about 6.9 million rows over all dates, so Lens opens it on
 * the same last 4 weeks as the send tabs.
 */
export function defaultSheetFrom(view: ViewKey, now = new Date()): string {
  return view === 'regions' || view === 'vendors' ? CLICK_SOURCE_START : defaultSendFrom(now);
}

export type SheetView = 'regions' | 'offers' | 'vendors';
export const isWorksheetDateView = (view: ViewKey): view is SheetView =>
  view === 'regions' || view === 'offers' || view === 'vendors';

type SheetDateKey = 'regionsFrom' | 'regionsTo' | 'offersFrom' | 'offersTo' | 'vendorsFrom' | 'vendorsTo';
/** The tab's own pair of state and URL keys. */
export const SHEET_KEYS: Record<SheetView, { from: SheetDateKey; to: SheetDateKey }> = {
  regions: { from: 'regionsFrom', to: 'regionsTo' },
  offers: { from: 'offersFrom', to: 'offersTo' },
  vendors: { from: 'vendorsFrom', to: 'vendorsTo' },
};

/** The tab's send dates: its own edited dates, otherwise its Tableau default. Other tabs fall back to the default. */
export function sheetRange(view: ViewKey, state: ReportState, now = new Date()): { from: string; to: string } {
  const keys = isWorksheetDateView(view) ? SHEET_KEYS[view] : undefined;
  return {
    from: (keys && state[keys.from]) || defaultSheetFrom(view, now),
    to: (keys && state[keys.to]) || lastCompleteDay(now),
  };
}

/**
 * Longest Offer Clicks window the page will request. Its two worksheets keep one row per send date and offer:
 * over Oct 2025 to Oct 2026 the busiest 42 days held 197,675 rows each (395,350 together) and the busiest 56 days
 * 262,369 each, past the 500,000 rows a worksheet card can carry.
 */
export const OFFER_CLICKS_MAX_DAYS = 42;

export function sheetProblem(view: ViewKey, range: { from: string; to: string }): string | null {
  if (view !== 'offers' || !/^\d{4}-\d{2}-\d{2}$/.test(range.from)) {
    return null;
  }
  return range.from < addDays(range.to, -(OFFER_CLICKS_MAX_DAYS - 1))
    ? `Offer Clicks can show up to ${OFFER_CLICKS_MAX_DAYS} days of send dates at a time. Choose a shorter range.`
    : null;
}
